import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Printer, Plus, Edit, Trash2, TestTube, Info } from 'lucide-react';
import axios from 'axios';

import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';

const PrintersTab = () => {
  const { getAuthHeaders } = useAuth();
  const [printers, setPrinters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    printer_name: '',
    station: 'kitchen',
    category_ids: [],
    pos_id: '',
    copies: 1
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPrinters();
    loadCategories();
  }, []);

  const loadPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers`, {
        headers: getAuthHeaders()
      });
      setPrinters(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading printers:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeaders()
      });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const getCategoriesForPrinter = (printer) => {
    if (!printer.category_ids || printer.category_ids.length === 0) {
      return [];
    }
    return categories.filter(c => printer.category_ids.includes(c.id));
  };

  const handleOpenDialog = (printer = null) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name,
        printer_name: printer.printer_name || 'default',
        station: printer.station,
        category_ids: printer.category_ids || [],
        pos_id: printer.pos_id || '',
        copies: printer.copies ?? 1
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        printer_name: 'default',
        station: 'kitchen',
        category_ids: [],
        pos_id: '',
        copies: 1
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        printer_name: formData.printer_name || 'default',
        pos_id: formData.station === 'pos' ? (formData.pos_id || null) : null,
        copies: parseInt(formData.copies, 10) || 1
      };

      if (editingPrinter) {
        await axios.put(
          `${API}/printers/${editingPrinter.id}`,
          dataToSend,
          { headers: getAuthHeaders() }
        );
        toast.success('Impresora actualizada');
      } else {
        await axios.post(
          `${API}/printers`,
          dataToSend,
          { headers: getAuthHeaders() }
        );
        toast.success('Impresora creada');
      }

      setShowDialog(false);
      loadPrinters();
    } catch (error) {
      console.error('Error saving printer:', error);
      toast.error(getErrorMessage(error, 'Error al guardar'));
    }
    setLoading(false);
  };

  const handleDelete = async (printer) => {
    if (!window.confirm(`¿Eliminar "${printer.name}"?`)) return;

    try {
      await axios.delete(`${API}/printers/${printer.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Impresora eliminada');
      loadPrinters();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar'));
    }
  };

  const testPrint = (printer) => {
    const printWindow = window.open('', '_blank', 'width=300,height=400');
    if (!printWindow) {
      toast.error('Permite las ventanas emergentes para imprimir');
      return;
    }

    const now = new Date();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Test - ${printer.name}</title>
        <style>
          body { 
            font-family: 'Courier New', monospace; 
            padding: 10px; 
            font-size: 12px;
            max-width: 280px;
            margin: 0 auto;
          }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .title { font-size: 16px; font-weight: bold; }
          .info { margin: 5px 0; }
          .footer { text-align: center; border-top: 1px dashed #000; padding-top: 10px; margin-top: 10px; font-size: 10px; }
          .success { font-size: 14px; margin: 20px 0; text-align: center; }
          @media print {
            body { margin: 0; padding: 5px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">🖨️ TEST DE IMPRESIÓN</div>
        </div>
        <div class="info"><strong>Impresora:</strong> ${printer.name}</div>
        <div class="info"><strong>Estación:</strong> ${printer.station}</div>
        <div class="info"><strong>Fecha:</strong> ${now.toLocaleDateString('es-ES')}</div>
        <div class="info"><strong>Hora:</strong> ${now.toLocaleTimeString('es-ES')}</div>
        <div class="success">✅ La impresión funciona correctamente</div>
        <div class="footer">SmartDine - Sistema de Gestión</div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 1000);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Prueba enviada a impresión');
  };

  const getStationBadge = (station) => {
    const badges = {
      kitchen: { label: 'Cocina', color: 'bg-orange-100 text-orange-800' },
      bar: { label: 'Bar', color: 'bg-blue-100 text-blue-800' },
      desserts: { label: 'Postres', color: 'bg-pink-100 text-pink-800' },
      grill: { label: 'Parrilla', color: 'bg-red-100 text-red-800' },
      pos: { label: 'POS', color: 'bg-purple-100 text-purple-800' }
    };
    const badge = badges[station] || { label: station, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={`${badge.color} rounded-full`}>{badge.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Impresoras</h2>
          <p className="text-gray-600">Configura las estaciones de impresión</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Impresora
        </Button>
      </div>

      {/* Guía Rápida */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Guía Rápida de Configuración</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Crea una impresora y asígnala a una estación (Cocina, Bar, etc.)</li>
              <li>Selecciona las categorías de productos que imprimirá</li>
              <li>Usa el botón &quot;Probar&quot; para verificar que funciona</li>
              <li>Al crear órdenes, las comandas se enviarán automáticamente</li>
            </ol>
            <p className="text-xs text-blue-600 mt-2">
              💡 Si el terminal tiene una impresora de estación POS asignada, los tickets y el cierre de caja salen directos; si no, se abre el diálogo de impresión del navegador.
            </p>
          </div>
        </div>
      </Card>

      {/* Lista de Impresoras */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {printers.map((printer) => (
          <Card
            key={printer.id}
            className="p-4 bg-white/80 backdrop-blur-xl border-orange-100 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Printer className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-800 truncate">{printer.name}</h3>
                  {getStationBadge(printer.station)}
                </div>
                
                {/* Categorías */}
                {getCategoriesForPrinter(printer).length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {getCategoriesForPrinter(printer).map((cat) => (
                      <Badge key={cat.id} variant="outline" className="text-xs">
                        {cat.icon} {cat.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-2">Sin categorías asignadas</p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => testPrint(printer)}
                variant="outline"
                size="sm"
                className="flex-1 border-green-200 hover:bg-green-50 text-green-600 rounded-xl"
              >
                <TestTube className="w-4 h-4 mr-1" />
                Probar
              </Button>
              <Button
                onClick={() => handleOpenDialog(printer)}
                variant="outline"
                size="sm"
                className="border-blue-200 hover:bg-blue-50 text-blue-600 rounded-xl"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => handleDelete(printer)}
                variant="outline"
                size="sm"
                className="border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {printers.length === 0 && (
        <Card className="p-8 text-center bg-white/80">
          <Printer className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No hay impresoras configuradas</p>
          <p className="text-sm text-gray-400">Agrega una impresora para empezar a imprimir comandas</p>
        </Card>
      )}

      {/* Dialog Simplificado */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPrinter ? 'Editar Impresora' : 'Nueva Impresora'}
            </DialogTitle>
            <DialogDescription>
              {editingPrinter
                ? 'Modifica la configuración de la impresora'
                : 'Configura una nueva impresora para la estación de trabajo'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Cocina Principal"
                className="rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="printer_name">Nombre de Impresora en Windows *</Label>
              <Input
                id="printer_name"
                value={formData.printer_name}
                onChange={(e) => setFormData({ ...formData, printer_name: e.target.value })}
                placeholder="Ej: iR1643i (debe coincidir con Windows)"
                className="rounded-xl"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Debe ser el nombre exacto que aparece en Configuración &gt; Dispositivos &gt; Impresoras</p>
            </div>

            <div>
              <Label htmlFor="station">Estación *</Label>
              <Select
                value={formData.station}
                onValueChange={(value) => setFormData({ ...formData, station: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">🍳 Cocina</SelectItem>
                  <SelectItem value="bar">🍹 Bar</SelectItem>
                  <SelectItem value="desserts">🍰 Postres</SelectItem>
                  <SelectItem value="grill">🔥 Parrilla</SelectItem>
                  <SelectItem value="pos">🖨️ POS (Caja)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Copias a imprimir</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={formData.copies}
                onChange={(e) => setFormData({ ...formData, copies: parseInt(e.target.value, 10) || 1 })}
                className="rounded-xl w-24"
              />
              <p className="text-xs text-gray-500 mt-1">Número de copias que se imprimirán automáticamente</p>
            </div>

            {formData.station === 'pos' && (
              <div>
                <Label htmlFor="pos_id">ID del Terminal POS *</Label>
                <Input
                  id="pos_id"
                  value={formData.pos_id}
                  onChange={(e) => setFormData({ ...formData, pos_id: e.target.value })}
                  placeholder="UUID del equipo POS (ej: caja-01)"
                  className="rounded-xl"
                />
                <p className="text-xs text-gray-500 mt-1">Debe coincidir con el ID configurado en el POS</p>
              </div>
            )}

            {formData.station !== 'pos' && (
            <div>
              <Label>Categorías que imprime</Label>
              <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-2">No hay categorías</p>
                ) : (
                  <div className="space-y-1">
                    {categories.map((category) => (
                      <label
                        key={category.id}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.category_ids.includes(category.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                category_ids: [...formData.category_ids, category.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                category_ids: formData.category_ids.filter(id => id !== category.id)
                              });
                            }
                          }}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm">{category.icon} {category.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Las comandas de estas categorías se enviarán a esta impresora
              </p>
            </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrintersTab;
